// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { combineEngineMetricResults, labelEngineMetricResult } from "./utils.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  isRotaxEngineContext,
} from "../core/engine-family.js";
import {
  clampScore,
  finiteValues,
  groundContactStats,
  median,
  peerAverage,
  pointCylinderValues,
  rankedQuantile as quantile,
  rollingMedianStep,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  egtRhythmAmplitudeSeverity,
  egtRhythmParameters,
} from "./detectors/egt-rhythm.js";

function rangeOf(values) {
  const extent = extentFromValues(values);
  return extent ? extent.max - extent.min : null;
}

function linearDetrend(points) {
  const n = points.length;
  if (n < 2) return points.map((point) => point.value);
  const meanT = points.reduce((total, point) => total + point.t, 0) / n;
  const meanY = points.reduce((total, point) => total + point.value, 0) / n;
  const denominator = points.reduce((total, point) => total + (point.t - meanT) ** 2, 0);
  const slope = denominator ? points.reduce((total, point) => total + (point.t - meanT) * (point.value - meanY), 0) / denominator : 0;
  return points.map((point) => (point.value - meanY) - slope * (point.t - meanT));
}

function smoothSeries(values, radius = egtRhythmParameters.smoothingRadius) {
  return values.map((_, index) => {
    const start = Math.max(0, index - radius);
    const end = Math.min(values.length, index + radius + 1);
    return averageValues(values.slice(start, end));
  });
}

function countMeaningfulReversals(values, threshold = egtRhythmParameters.reversalThresholdF) {
  let direction = 0;
  let anchor = values[0] ?? 0;
  let reversals = 0;
  for (let index = 1; index < values.length; index += 1) {
    const delta = values[index] - anchor;
    if (Math.abs(delta) < threshold) continue;
    const nextDirection = Math.sign(delta);
    if (direction && nextDirection !== direction) reversals += 1;
    direction = nextDirection;
    anchor = values[index];
  }
  return reversals;
}

function pointGroundSpeed(point) {
  return ["groundSpeed", "ias", "tas"].map((key) => point[key]).find(Number.isFinite) ?? null;
}

function candidateWindowPhase(points) {
  const counts = new Map();
  points.forEach((point) => {
    if (!point.phase) return;
    counts.set(point.phase, (counts.get(point.phase) ?? 0) + 1);
  });
  let best = "Unknown";
  let bestCount = 0;
  counts.forEach((count, phase) => {
    if (count <= bestCount) return;
    best = phase;
    bestCount = count;
  });
  return best;
}

function engineModelTextForRhythmOptions(options = {}) {
  const engine = options.aircraftProfile?.engine ?? {};
  const engines = Array.isArray(engine.engines) ? engine.engines : [];
  return [
    engine.make,
    engine.model,
    options.parserMetadata?.engineMake,
    options.parserMetadata?.engineModel,
    ...engines.flatMap((entry) => [entry?.make, entry?.model]),
  ].map((value) => String(value ?? "").trim()).filter(Boolean).join(" ");
}

function rhythmProfileForOptions(options = {}) {
  if (!isRotaxEngineContext(options)) {
    return {
      engineFamily: "air-cooled-piston",
      interpretation: "exhaust-valve-review",
      candidate: egtRhythmParameters.candidate,
      scoring: egtRhythmParameters.scoring,
    };
  }
  const normalized = engineModelTextForRhythmOptions(options).toLowerCase().replace(/[\s_-]+/g, "");
  const rotax = egtRhythmParameters.rotax;
  const injectedPattern = new RegExp(rotax.injectedModelPattern, "i");
  const variant = injectedPattern.test(normalized) ? "injected" : "carb";
  return {
    engineFamily: "rotax",
    variant,
    interpretation: "egt-instability-review",
    candidate: {
      ...egtRhythmParameters.candidate,
      ...rotax[variant],
    },
    scoring: {
      ...egtRhythmParameters.scoring,
      ...rotax.scoring,
    },
  };
}

function rhythmCandidateSuspicion(candidate, config) {
  const band = candidate.frequency?.band ?? "unclassified";
  const hasFrequencyPattern = band !== "unclassified";
  const cyclesInWindow = candidate.frequency?.cyclesInWindow;
  const enoughCycleEvidence = candidate.cycles >= config.minCycles
    || (band === "slow" && Number.isFinite(cyclesInWindow) && cyclesInWindow >= config.minSlowCycles)
    || (band === "fast" && Number.isFinite(cyclesInWindow) && cyclesInWindow >= config.minFastCycles)
    || (band === "mixed" && Number.isFinite(cyclesInWindow) && cyclesInWindow >= config.minMixedCycles);
  const meetsPeerRatio = candidate.ratio >= config.minPeerRatio;
  const strongAmplitude = Number.isFinite(config.strongAmplitudeF) && candidate.amplitude >= config.strongAmplitudeF;
  const meetsBaselineRatio = !Number.isFinite(config.minBaselineRatio) || candidate.baselineRatio >= config.minBaselineRatio || strongAmplitude;
  const qualifies = candidate.amplitude >= config.minAmplitudeF
    && meetsPeerRatio
    && meetsBaselineRatio
    && enoughCycleEvidence
    && hasFrequencyPattern;
  if (!qualifies) return 0;
  return config.suspicionBase
    + Math.max(0, candidate.amplitude - config.minAmplitudeF) * config.amplitudeWeight
    + Math.max(0, candidate.ratio - config.minPeerRatio) * config.ratioWeight
    + Math.max(0, candidate.baselineRatio - (config.minBaselineRatio ?? 1)) * (config.baselineRatioWeight ?? 0)
    + Math.max(0, candidate.cycles - config.minCycles) * config.cycleWeight
    + (config.bandBonus[band] ?? 0);
}

function isFitOnlySlowCandidate(candidate, config) {
  return candidate.suspicion > 0
    && candidate.frequency?.band === "slow"
    && candidate.cycles < config.minCycles;
}

function independentRhythmWindowCount(items) {
  let end = -Infinity;
  let count = 0;
  // Earliest-ending intervals select the largest set with no shared samples.
  for (const item of [...items].sort((a, b) => a.endTime - b.endTime)) {
    if (item.startTime <= end) continue;
    end = item.endTime;
    count += 1;
  }
  return count;
}

function phaseForWindow(points, options = {}) {
  return options.phaseForWindow?.(points) ?? candidateWindowPhase(points);
}

function isStableEgtWindow(window) {
  const config = egtRhythmParameters.stableWindow;
  if (window.length < config.minSamples) return false;
  const first = window[0];
  const last = window.at(-1);
  const duration = (last.elapsedSeconds ?? window.length - 1) - (first.elapsedSeconds ?? 0);
  if (duration < config.minDurationSeconds) return false;

  const egtAverages = window.map((point) => averageValues(pointCylinderValues(point))).filter(Number.isFinite);
  if (egtAverages.length < window.length * config.minEgtCoverageShare) return false;
  const averageEgt = averageValues(egtAverages);
  if (!Number.isFinite(averageEgt) || averageEgt < config.minAverageEgtF) return false;
  if ((rangeOf(egtAverages) ?? 0) > config.maxEgtRangeF) return false;
  if (Math.abs(egtAverages.at(-1) - egtAverages[0]) / Math.max(duration, 1) > config.maxEgtDriftFps) return false;

  const rpmValues = window.map((point) => point.rpm).filter(Number.isFinite);
  if (rpmValues.length >= window.length * config.powerCoverageShare && (rangeOf(rpmValues) ?? 0) > Math.max(config.maxRpmRange, (median(rpmValues) ?? 0) * config.maxRpmRangeShare)) return false;

  const mapValues = window.map((point) => point.map).filter(Number.isFinite);
  if (mapValues.length >= window.length * config.powerCoverageShare && (rangeOf(mapValues) ?? 0) > config.maxMapRangeInHg) return false;

  const fuelValues = window.map((point) => point.fuelFlow).filter(Number.isFinite);
  if (fuelValues.length >= window.length * config.powerCoverageShare && (rangeOf(fuelValues) ?? 0) > Math.max(config.maxFuelFlowRangeGph, (median(fuelValues) ?? 0) * config.maxFuelFlowRangeShare)) return false;

  return true;
}

function findStableEgtWindows(data, windowSeconds = egtRhythmParameters.stableWindow.windowSeconds) {
  const config = egtRhythmParameters.stableWindow;
  const step = rollingMedianStep(data);
  const sampleCount = Math.max(config.minWindowSamples, Math.round(windowSeconds / step));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  const windows = [];
  for (let start = 0; start + sampleCount <= data.length; start += stride) {
    const window = data.slice(start, start + sampleCount);
    if (isStableEgtWindow(window)) windows.push({ start, end: start + sampleCount - 1, points: window, windowSeconds });
  }
  return windows;
}

function isValvePatternWindow(window, options = {}) {
  const config = egtRhythmParameters.valveWindow;
  if (!isStableEgtWindow(window)) return false;
  const first = window[0] ?? {};
  const startTime = first.elapsedSeconds ?? 0;
  const phase = phaseForWindow(window, options);
  const contact = groundContactStats(window);
  if (contact.known && (contact.groundShare ?? 0) > 0.1) return false;

  const speedValues = window.map(pointGroundSpeed).filter(Number.isFinite);
  const speedMedian = median(speedValues);

  const rpmValues = window.map((point) => point.rpm).filter(Number.isFinite);
  const rpmMedian = median(rpmValues);
  if (rpmValues.length >= window.length * config.minRpmCoverageShare && rpmMedian < config.minRpm) return false;

  const fuelValues = window.map((point) => point.fuelFlow).filter(Number.isFinite);
  const fuelMedian = median(fuelValues);
  if (fuelValues.length >= window.length * config.minFuelCoverageShare && fuelMedian < config.minFuelFlowGph) return false;

  const hasLoadedContext = (Number.isFinite(speedMedian) && speedMedian > config.minLoadedGroundSpeedKt)
    || (Number.isFinite(rpmMedian) && rpmMedian >= config.loadedRpm)
    || (Number.isFinite(fuelMedian) && fuelMedian >= config.loadedFuelFlowGph);
  const ambiguousLoadedGroundRun = phase === "Ground Run"
    && !contact.known
    && startTime >= config.earlyWindowSeconds
    && hasLoadedContext;
  if (config.excludedPhases.includes(phase) && !ambiguousLoadedGroundRun) return false;
  if (
    speedValues.length >= window.length * config.minSpeedCoverageShare
    && speedMedian <= config.minLoadedGroundSpeedKt
    && !ambiguousLoadedGroundRun
  ) return false;
  if (startTime < config.earlyWindowSeconds && !hasLoadedContext) return false;

  return true;
}

function findValvePatternWindows(data, options = {}) {
  const seen = new Set();
  return egtRhythmParameters.valveWindow.windowSeconds
    .flatMap((seconds) => findStableEgtWindows(data, seconds))
    .filter((window) => isValvePatternWindow(window.points, options))
    .filter((window) => {
      const key = `${window.start}-${window.end}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

// Fit an offset plus sine and cosine jointly. Centering the basis preserves the
// cross term on partial cycles and irregular samples; local time avoids phase
// precision loss when the evidence window occurs late in a flight.
export function fitEgtRhythmFrequency(samples, frequencyCpm) {
  const config = egtRhythmParameters.frequency;
  if (!Number.isFinite(frequencyCpm) || frequencyCpm <= 0 || samples.length < config.minFitSamples) return null;
  const origin = samples[0].t;
  const cycleSeconds = 60 / frequencyCpm;
  let maxGap = 0;
  for (let i = 0; i < samples.length; i += 1) {
    if (!Number.isFinite(samples[i].t) || !Number.isFinite(samples[i].y)) return null;
    if (i === 0) continue;
    const gap = samples[i].t - samples[i - 1].t;
    if (gap <= 0) return null;
    maxGap = Math.max(maxGap, gap);
  }
  // Use the largest actual gap, including channel dropouts, so bursts of dense
  // samples cannot make an otherwise unresolved frequency appear measurable.
  if (maxGap * config.minSamplesPerCycle > cycleSeconds) return null;
  const omega = 2 * Math.PI / cycleSeconds;
  const basis = samples.map(({ t, y }) => ({ s: Math.sin(omega * (t - origin)), c: Math.cos(omega * (t - origin)), y }));
  const meanS = averageValues(basis.map(p => p.s));
  const meanC = averageValues(basis.map(p => p.c));
  const meanY = averageValues(basis.map(p => p.y));
  let sinSin = 0;
  let cosCos = 0;
  let sinCos = 0;
  let ySin = 0;
  let yCos = 0;
  let energy = 0;
  basis.forEach(({ s, c, y }) => {
    s -= meanS;
    c -= meanC;
    y -= meanY;
    sinSin += s * s;
    cosCos += c * c;
    sinCos += s * c;
    ySin += y * s;
    yCos += y * c;
    energy += y * y;
  });
  const determinant = sinSin * cosCos - sinCos * sinCos;
  const trace = sinSin + cosCos;
  const condition = determinant / (trace * trace);
  if (!(energy > 0) || !Number.isFinite(condition) || condition < config.minFitCondition) return null;
  const sinCoeff = (ySin * cosCos - yCos * sinCos) / determinant;
  const cosCoeff = (yCos * sinSin - ySin * sinCos) / determinant;
  const offset = meanY - sinCoeff * meanS - cosCoeff * meanC;
  const peakToPeak = 2 * Math.hypot(sinCoeff, cosCoeff);
  const error = basis.reduce((sum, p) => sum + (p.y - offset - sinCoeff * p.s - cosCoeff * p.c) ** 2, 0);
  const explainedVariance = Math.max(0, Math.min(1, 1 - error / energy));
  if (!Number.isFinite(peakToPeak) || !Number.isFinite(explainedVariance)) return null;
  return { frequencyCpm, cycleSeconds, peakToPeak, explainedVariance, condition,
    samplesPerCycle: cycleSeconds / maxGap, origin, omega, offset, sinCoeff, cosCoeff };
}

function fitBandAmplitude(samples, frequenciesCpm) {
  let best = null;
  frequenciesCpm.forEach((frequencyCpm) => {
    const fit = fitEgtRhythmFrequency(samples, frequencyCpm);
    if (!fit || fit.explainedVariance < egtRhythmParameters.frequency.minExplainedVariance) return;
    if (!best || fit.explainedVariance > best.explainedVariance) {
      const { cycleSeconds, peakToPeak, explainedVariance, samplesPerCycle } = fit;
      best = { frequencyCpm, cycleSeconds, peakToPeak, explainedVariance, samplesPerCycle };
    }
  });
  return best;
}

function frequencyRange(start, end, step) {
  const values = [];
  for (let value = start; value <= end + step / 2; value += step) {
    values.push(Number(value.toFixed(3)));
  }
  return values;
}

function valveFrequencyFeatures(pairs, residual, amplitude, rms) {
  const config = egtRhythmParameters.frequency;
  if (pairs.length !== residual.length || residual.length < config.minResidualSamples) return null;
  const maxGap = pairs.slice(1).reduce((gap, pair, index) => {
    const delta = pair.t - pairs[index].t;
    return delta > 0 && Number.isFinite(delta) ? Math.max(gap, delta) : Infinity;
  }, 0);
  if (maxGap * config.minSamplesPerCycle > 60 / Math.min(config.slowStartCpm, config.fastStartCpm)) return null;
  const firstTime = pairs[0]?.t ?? 0;
  const lastTime = pairs.at(-1)?.t ?? firstTime;
  const durationSeconds = Math.max(1, lastTime - firstTime);
  const mean = averageValues(residual);
  const samples = residual.map((value, index) => ({
    t: (pairs[index]?.t ?? index) - firstTime,
    y: value - (mean || 0),
  }));
  const slow = fitBandAmplitude(samples, frequencyRange(config.slowStartCpm, config.slowEndCpm, config.slowStepCpm));
  const fast = fitBandAmplitude(samples, frequencyRange(config.fastStartCpm, config.fastEndCpm, config.fastStepCpm));
  const minimumPeakToPeak = Math.max(config.minPeakToPeakFloorF, amplitude * config.amplitudeScale, rms * config.rmsScale);
  const slowStrong = Boolean(slow && slow.peakToPeak >= minimumPeakToPeak);
  const fastStrong = Boolean(fast && fast.peakToPeak >= minimumPeakToPeak);
  let band = "unclassified";
  let dominant = null;
  if (slowStrong && fastStrong) {
    const high = Math.max(slow.peakToPeak, fast.peakToPeak);
    const low = Math.min(slow.peakToPeak, fast.peakToPeak);
    band = low / high >= config.mixedPeakRatio ? "mixed" : slow.peakToPeak > fast.peakToPeak ? "slow" : "fast";
    dominant = band === "fast" ? fast : slow;
  } else if (slowStrong) {
    band = "slow";
    dominant = slow;
  } else if (fastStrong) {
    band = "fast";
    dominant = fast;
  }
  if (!dominant && (slow || fast)) dominant = (slow?.peakToPeak ?? 0) >= (fast?.peakToPeak ?? 0) ? slow : fast;
  return {
    band,
    durationSeconds,
    minimumPeakToPeak,
    slow,
    fast,
    frequencyCpm: dominant?.frequencyCpm ?? null,
    cycleSeconds: dominant?.cycleSeconds ?? null,
    cyclesInWindow: dominant?.frequencyCpm ? dominant.frequencyCpm * durationSeconds / 60 : null,
  };
}

export function valveBandLabel(band) {
  return {
    slow: "slow wobble",
    fast: "fast wobble",
    mixed: "mixed wobble",
  }[band] ?? "unclassified wobble";
}

export function valveFrequencyLabel(frequency) {
  if (!frequency || frequency.band === "unclassified" || !Number.isFinite(frequency.frequencyCpm)) {
    return "No dominant frequency band";
  }
  const cycle = Number.isFinite(frequency.cycleSeconds)
    ? `, about ${Math.round(frequency.cycleSeconds)} sec/cycle`
    : "";
  return `${valveBandLabel(frequency.band)} at ${frequency.frequencyCpm.toFixed(1)} cycles/min${cycle}`;
}

function cylinderOscillationMetric(window, key, egtKeys) {
  const config = egtRhythmParameters.oscillation;
  const pairs = window.map((point) => {
    const value = point[key];
    const common = averageValues(pointCylinderValues(point, egtKeys));
    if (!Number.isFinite(value) || !Number.isFinite(common) || !Number.isFinite(point.elapsedSeconds)) return null;
    return {
      t: point.elapsedSeconds,
      value: value - common,
    };
  }).filter(Boolean);

  if (pairs.length < window.length * config.minCoverageShare || pairs.length < config.minSamples) return null;
  const residual = smoothSeries(linearDetrend(pairs), egtRhythmParameters.smoothingRadius).filter(Number.isFinite);
  if (residual.length < config.minSamples) return null;
  const low = quantile(residual, config.lowQuantile) ?? 0;
  const high = quantile(residual, config.highQuantile) ?? 0;
  const amplitude = high - low;
  const rms = Math.sqrt(residual.reduce((total, value) => total + value ** 2, 0) / residual.length);
  const reversals = countMeaningfulReversals(residual, egtRhythmParameters.reversalThresholdF);
  const frequency = valveFrequencyFeatures(pairs, residual, amplitude, rms);
  if (!frequency) return null;
  return {
    amplitude,
    rms,
    reversals,
    cycles: reversals / 2,
    frequency,
  };
}

function valveCrossFlightPersistence(top, options = {}) {
  const config = egtRhythmParameters.crossFlight;
  const cylinder = top?.cylinder;
  if (!Number.isFinite(cylinder) || !top?.best?.suspicion) {
    return { recentRows: 0, matches: [], repeatCount: 0, priorCount: 0, penalty: 0 };
  }
  const entry = options.selectedEntry;
  const currentId = entry?.id ?? null;
  const rowMap = new Map();
  [...(options.crossFlightRows ?? []), ...(options.staticValveRowsForAircraft?.(entry) ?? [])]
    .filter((row) => row && !row.error)
    .forEach((row) => {
      if (!rowMap.has(row.id)) rowMap.set(row.id, row);
    });
  const rows = [...rowMap.values()].filter((row) => row.aircraft === entry?.aircraft);
  const currentDate = options.trendDateValue?.(entry ?? {}, []) ?? 0;
  const recentRows = rows
    .filter((row) => currentDate <= 0 || !Number.isFinite(row.dateValue) || row.dateValue <= currentDate + config.futureToleranceHours * 60 * 60 * 1000)
    .sort((a, b) => (b.dateValue ?? 0) - (a.dateValue ?? 0))
    .slice(0, config.historyLookbackRows);
  const matches = recentRows.filter((row) => (
    row.valveCylinder === cylinder
    && Number.isFinite(row.valveScore)
    && row.valveScore < config.reviewScoreThreshold
  ));
  const priorCount = matches.filter((row) => row.id !== currentId).length;
  const repeatCount = matches.length;
  const penalty = repeatCount >= config.minRepeatCount
    ? Math.min(config.penaltyCap, priorCount * config.priorPenaltyWeight + Math.max(0, repeatCount - config.minRepeatCount) * config.extraRepeatPenaltyWeight)
    : 0;
  return {
    recentRows: recentRows.length,
    matches,
    repeatCount,
    priorCount,
    penalty,
  };
}

function nearestPointByElapsed(data, elapsed) {
  if (!data.length || !Number.isFinite(elapsed)) return null;
  return data.reduce((best, point) => {
    const bestDelta = Math.abs((best.elapsedSeconds ?? 0) - elapsed);
    const pointDelta = Math.abs((point.elapsedSeconds ?? 0) - elapsed);
    return pointDelta < bestDelta ? point : best;
  }, data[0]);
}

function valveWindowTiming(data, top, options = {}) {
  const best = top?.best;
  if (!best || !Number.isFinite(best.startTime) || !Number.isFinite(best.endTime)) return null;
  const rows = data.filter((point) => {
    const elapsed = point.elapsedSeconds;
    return Number.isFinite(elapsed) && elapsed >= best.startTime && elapsed <= best.endTime;
  });
  const startPoint = rows[0] ?? nearestPointByElapsed(data, best.startTime);
  const endPoint = rows.at(-1) ?? nearestPointByElapsed(data, best.endTime);
  const startClock = options.formatPointTime?.(startPoint);
  const endClock = options.formatPointTime?.(endPoint);
  const hasClock = startClock && endClock && startClock !== "--" && endClock !== "--";
  const elapsedLabel = `${formatElapsedTime(best.startTime)}-${formatElapsedTime(best.endTime)}`;
  const windowLabel = hasClock ? `${startClock}-${endClock}` : elapsedLabel;
  const phase = phaseForWindow(rows.length ? rows : [startPoint, endPoint].filter(Boolean), options);
  const speedValues = rows.map(pointGroundSpeed).filter(Number.isFinite);
  const speedMedian = median(speedValues);
  const contact = groundContactStats(rows);
  const rpmMedian = median(rows.map((point) => point.rpm).filter(Number.isFinite));
  const fuelMedian = median(rows.map((point) => point.fuelFlow).filter(Number.isFinite));
  const lowSpeed = Number.isFinite(speedMedian) && speedMedian <= 20;
  const groundContactLabel = contact.known
    ? (contact.groundShare ?? 0) > 0.1 ? "ground-contact mixed" : "ground-contact airborne"
    : "";
  const phaseLabel = phase && phase !== "Unknown" ? phase : "stable window";
  const operationLabel = lowSpeed
    ? `${phaseLabel} label, low speed`
    : phaseLabel;
  const contextParts = [
    Number.isFinite(rpmMedian) ? `RPM ~${Math.round(rpmMedian)}` : "",
    Number.isFinite(fuelMedian) ? `FF ~${fuelMedian.toFixed(1)} GPH` : "",
    Number.isFinite(speedMedian) ? `GS ~${Math.round(speedMedian)} kt` : "",
    groundContactLabel,
  ].filter(Boolean);

  return {
    startTime: best.startTime,
    endTime: best.endTime,
    duration: best.endTime - best.startTime,
    elapsedLabel,
    windowLabel,
    clockLabel: hasClock ? windowLabel : "",
    phase: phaseLabel,
    operationLabel,
    contextLabel: contextParts.join(" · "),
    durationLabel: formatElapsedTime(best.endTime - best.startTime),
    rpmMedian,
    fuelMedian,
    speedMedian,
  };
}

function neutralSensorSummary() {
  return {
    suspicion: 0,
    coldMismatchRatio: 0,
    missingRatio: 0,
    zeroCount: 0,
    outOfRangeCount: 0,
    impossibleJumpCount: 0,
  };
}

function valveProbeEvidence(data, top, options = {}) {
  const config = egtRhythmParameters.probeEvidence;
  const cylinder = top?.cylinder;
  const egtKey = top?.best?.key ?? (Number.isFinite(cylinder) ? `egt${cylinder}` : "");
  const chtKey = Number.isFinite(cylinder) ? `cht${cylinder}` : "";
  if (!egtKey || !chtKey) return null;

  const workRows = data.filter(engineWorkPoint);
  const rows = workRows.length >= config.minWorkRows ? workRows : data;
  if (!rows.length) return null;

  const egtPeerKeys = options.egtKeys ?? cylinderKeys("egt");
  const chtPeerKeys = options.chtKeys ?? cylinderKeys("cht");
  const sensor = options.summarizeSensorChannel?.(rows, egtKey, egtPeerKeys) ?? neutralSensorSummary();
  const chtValues = finiteValues(rows, chtKey);
  const chtMax = extentFromValues(chtValues)?.max ?? null;
  const chtP95 = quantile(chtValues, 0.95);
  const chtPeerDeltas = rows.map((point) => {
    const value = point[chtKey];
    const peer = peerAverage(point, chtPeerKeys, chtKey);
    return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
  }).filter(Number.isFinite);
  const chtPeerP95 = quantile(chtPeerDeltas, 0.95);
  const chtPeerP05 = quantile(chtPeerDeltas, 0.05);
  const sameChtNormal = Number.isFinite(chtP95)
    && chtP95 < config.maxNormalChtP95F
    && (!Number.isFinite(chtMax) || chtMax < config.maxNormalChtF)
    && (!Number.isFinite(chtPeerP95) || chtPeerP95 < config.maxNormalChtPeerP95F)
    && (!Number.isFinite(chtPeerP05) || chtPeerP05 > config.minNormalChtPeerP05F);
  const coldEgtMismatch = sensor.coldMismatchRatio >= config.coldMismatchRatio;
  const chtCorroboratesColdEgt = Number.isFinite(chtPeerP05) && chtPeerP05 <= config.corroboratingColdChtPeerP05F;
  const coldEgtWithoutColdCht = coldEgtMismatch && !chtCorroboratesColdEgt;

  const symptoms = [];
  if (sensor.coldMismatchRatio >= config.coldSymptomRatio) symptoms.push(`${Math.round(sensor.coldMismatchRatio * 100)}% cold versus peer EGTs`);
  if (coldEgtWithoutColdCht) symptoms.push("CHT does not corroborate the cold EGT split");
  if (sensor.missingRatio >= config.missingRatio) symptoms.push(`${Math.round(sensor.missingRatio * 100)}% missing`);
  if (sensor.zeroCount) symptoms.push(`${sensor.zeroCount.toLocaleString()} zero/dropout sample${sensor.zeroCount === 1 ? "" : "s"}`);
  if (sensor.outOfRangeCount) symptoms.push(`${sensor.outOfRangeCount.toLocaleString()} out-of-range sample${sensor.outOfRangeCount === 1 ? "" : "s"}`);
  if (sensor.impossibleJumpCount) symptoms.push(`${sensor.impossibleJumpCount.toLocaleString()} abrupt data jump${sensor.impossibleJumpCount === 1 ? "" : "s"}`);

  const probePattern = sensor.coldMismatchRatio >= config.coldMismatchRatio
    || sensor.missingRatio >= config.probeMissingRatio
    || sensor.zeroCount > 0
    || sensor.outOfRangeCount > 0
    || sensor.impossibleJumpCount >= config.impossibleJumpCount
    || sensor.suspicion >= config.suspicionThreshold;

  return {
    key: egtKey,
    label: options.labelForKey?.(egtKey) ?? egtKey.toUpperCase(),
    chtLabel: options.labelForKey?.(chtKey) ?? chtKey.toUpperCase(),
    sameChtNormal,
    likelyProbeIssue: probePattern && (sameChtNormal || coldEgtWithoutColdCht),
    symptoms,
    sensor,
    chtMax,
    chtP95,
    chtPeerP95,
    chtPeerP05,
  };
}

function analyzeExhaustValvePatternForKeys(data, egtSourceKeys, chtSourceKeys, options = {}) {
  const rhythmProfile = rhythmProfileForOptions(options);
  const candidateConfig = rhythmProfile.candidate;
  const scoring = rhythmProfile.scoring;
  const egtKeys = egtSourceKeys.filter((key) => data.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < 2) {
    return { key: "valve", score: null, note: "Need multiple EGT channels", details: {} };
  }

  const windows = findValvePatternWindows(data, options);
  if (!windows.length) {
    return { key: "valve", score: null, note: "This log does not include a later steady-power segment with enough EGT data for the EGT rhythm check.", details: { windows: 0 } };
  }

  const candidates = [];
  windows.forEach((window) => {
    const metrics = egtKeys.map((key) => ({
      key,
      cylinder: cylinderNumberFromKey(key),
      ...cylinderOscillationMetric(window.points, key, egtKeys),
    })).filter((metric) => Number.isFinite(metric.amplitude));
    const peerMedian = median(metrics.map((metric) => metric.amplitude));
    if (!Number.isFinite(peerMedian) || peerMedian <= 0) return;
    metrics.forEach((metric) => {
      const ratio = metric.amplitude / peerMedian;
      candidates.push({
        ...metric,
        ratio,
        peerMedian,
        startTime: window.points[0].elapsedSeconds ?? window.start,
        endTime: window.points.at(-1).elapsedSeconds ?? window.end,
      });
    });
  });

  if (!candidates.length) {
    return { key: "valve", score: null, note: "Insufficient EGT samples", details: { windows: windows.length } };
  }

  const byCylinder = new Map();
  candidates.forEach((candidate) => {
    if (!byCylinder.has(candidate.cylinder)) byCylinder.set(candidate.cylinder, []);
    byCylinder.get(candidate.cylinder).push(candidate);
  });
  const scoredCandidates = [];
  const cylinderSummaries = [...byCylinder.entries()].map(([cylinder, items]) => {
    const baselineAmplitude = median(items.map((item) => item.amplitude).filter(Number.isFinite));
    const initiallyScoredItems = items.map((item) => {
      const baselineRatio = Number.isFinite(baselineAmplitude) && baselineAmplitude > 0
        ? item.amplitude / baselineAmplitude
        : null;
      const scored = {
        ...item,
        baselineAmplitude,
        baselineRatio,
      };
      return {
        ...scored,
        suspicion: rhythmCandidateSuspicion(scored, candidateConfig),
      };
    });
    const fitOnlySlowItems = initiallyScoredItems.filter((item) => isFitOnlySlowCandidate(item, candidateConfig));
    const hasRepeatedSlowFit = independentRhythmWindowCount(fitOnlySlowItems) >= (candidateConfig.minSlowRepeatWindows ?? Infinity);
    const hasDirectCycleEvidence = initiallyScoredItems.some((item) => item.suspicion > 0 && !isFitOnlySlowCandidate(item, candidateConfig));
    const suppressFitOnlySlow = fitOnlySlowItems.length > 0 && !hasRepeatedSlowFit && !hasDirectCycleEvidence;
    const scoredItems = suppressFitOnlySlow
      ? initiallyScoredItems.map((item) => isFitOnlySlowCandidate(item, candidateConfig)
        ? {
          ...item,
          unsuppressedSuspicion: item.suspicion,
          suspicion: 0,
          suppressedReason: "isolated-slow-fit",
        }
        : item)
      : initiallyScoredItems;
    scoredCandidates.push(...scoredItems);
    const suspicious = scoredItems.filter((item) => item.suspicion > 0);
    const independentWindows = independentRhythmWindowCount(items);
    const independentSuspiciousWindows = independentRhythmWindowCount(suspicious);
    const best = [...scoredItems].sort((a, b) => b.suspicion - a.suspicion)[0];
    return {
      cylinder,
      best,
      suspiciousWindows: suspicious.length,
      independentWindows,
      independentSuspiciousWindows,
      persistence: independentSuspiciousWindows / Math.max(independentWindows, 1),
    };
  }).sort((a, b) => {
    const aScore = a.best.suspicion + a.independentSuspiciousWindows * candidateConfig.suspiciousWindowWeight;
    const bScore = b.best.suspicion + b.independentSuspiciousWindows * candidateConfig.suspiciousWindowWeight;
    return bScore - aScore;
  });

  const top = cylinderSummaries[0];
  const crossFlight = valveCrossFlightPersistence(top, options);
  const probeEvidence = valveProbeEvidence(data, top, { ...options, egtKeys, chtKeys: chtSourceKeys });
  const suspiciousScore = top.best.suspicion
    + Math.max(0, top.independentSuspiciousWindows - 1) * scoring.repeatWindowWeight
    + top.persistence * scoring.persistenceWeight
    + crossFlight.penalty;
  const rawScore = top.best.suspicion > 0
    ? clampScore(100 - Math.min(scoring.suspicionCap, suspiciousScore))
    : scoring.cleanScore;
  const score = probeEvidence?.likelyProbeIssue ? Math.max(rawScore, scoring.probeFloorScore) : rawScore;
  const amplitude = Math.round(top.best.amplitude);
  const amplitudeSeverity = score < scoring.reviewScoreThreshold
    ? egtRhythmAmplitudeSeverity(top.best.amplitude) === "warning" ? "warning" : "watch"
    : "normal";
  const ratio = top.best.ratio.toFixed(1);
  const timing = valveWindowTiming(data, top, options);
  const frequencyLabel = valveFrequencyLabel(top.best.frequency);
  const note = score < scoring.reviewScoreThreshold
    ? probeEvidence?.likelyProbeIssue
      ? `${probeEvidence.label} signal affects EGT-rhythm screen`
      : rhythmProfile.engineFamily === "rotax"
        ? `Cyl ${top.cylinder} EGT instability; ${amplitude} F, ${ratio}x${top.best.frequency?.frequencyCpm ? `, ${top.best.frequency.frequencyCpm.toFixed(1)} cpm` : ""}`
        : `Cyl ${top.cylinder} ${valveBandLabel(top.best.frequency?.band)}; ${amplitude} F, ${ratio}x${top.best.frequency?.frequencyCpm ? `, ${top.best.frequency.frequencyCpm.toFixed(1)} cpm` : ""}`
    : `${windows.length} stable check${windows.length === 1 ? "" : "s"}, no EGT rhythm cue`;

  return {
    key: "valve",
    score,
    note,
    details: {
      windows: windows.length,
      top,
      candidates: scoredCandidates,
      probeEvidence,
      timing,
      frequencyLabel,
      crossFlight,
      amplitudeSeverity,
      rhythmProfile: {
        engineFamily: rhythmProfile.engineFamily,
        variant: rhythmProfile.variant ?? null,
        interpretation: rhythmProfile.interpretation,
        minAmplitudeF: candidateConfig.minAmplitudeF,
        strongAmplitudeF: candidateConfig.strongAmplitudeF ?? null,
        warningAmplitudeF: egtRhythmParameters.severity.warningAmplitudeF,
        minPeerRatio: candidateConfig.minPeerRatio,
        minBaselineRatio: candidateConfig.minBaselineRatio ?? null,
      },
    },
  };
}

export function analyzeExhaustValvePattern(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const egtSourceKeys = engineCylinderKeys(engine, "egt");
      const chtSourceKeys = engineCylinderKeys(engine, "cht");
      const projected = projectFlightDataForEngine(data, engine);
      const result = labelEngineMetricResult(analyzeExhaustValvePatternForKeys(projected, egtSourceKeys, chtSourceKeys, options), descriptors);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        result,
      };
    }).filter(Boolean);
    const scoredEngines = perEngine.filter((entry) => Number.isFinite(entry.result.score));
    if (scoredEngines.length < 2) return combineEngineMetricResults(perEngine);
    if (scoredEngines.length >= 2) {
      const worst = scoredEngines.reduce((acc, entry) => acc === null || entry.result.score < acc.result.score ? entry : acc, null);
      const score = scoredEngines.reduce((acc, entry) => Math.min(acc, entry.result.score), 100);
      const note = worst.result.score < egtRhythmParameters.scoring.reviewScoreThreshold
        ? `${worst.shortLabel ? `${worst.shortLabel}: ` : ""}${worst.result.note}`
        : worst.result.note;
      return {
        key: "valve",
        score,
        note,
        details: {
          ...worst.result.details,
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: entry.result.score,
            note: entry.result.note,
            details: entry.result.details,
          })),
          worstEngineKey: worst.engineKey,
        },
      };
    }
  }
  return analyzeExhaustValvePatternForKeys(data, cylinderKeys("egt"), cylinderKeys("cht"), options);
}

// Least-squares sinusoid fit at one frequency, returning the evaluator y(t)
// instead of only the peak-to-peak amplitude that fitBandAmplitude keeps. Lets
// the Overview draw the actual fitted "valve-pattern" curve over the residual.
function fitCurveAtFrequency(samples, frequencyCpm) {
  const fit = fitEgtRhythmFrequency(samples, frequencyCpm);
  if (!fit || fit.explainedVariance < egtRhythmParameters.frequency.minExplainedVariance) return null;
  return (t) => fit.offset + fit.sinCoeff * Math.sin(fit.omega * (t - fit.origin))
    + fit.cosCoeff * Math.cos(fit.omega * (t - fit.origin));
}

// Per-cylinder EGT-rhythm waveforms for a single flight. The detector
// (cylinderOscillationMetric) scores peer-relative residuals and discards the
// samples, keeping only scalars; this re-runs the SAME peer-relative math for
// evidence while also retaining a display-only per-cylinder baseline series. The
// baseline series prevents the chart from making flat peer cylinders appear to
// dip simply because the affected cylinder raised the shared engine average.
//
// Scalars (amplitude / ratio / frequency) are read from the passed-in `details`
// (summary.valve.details) so the drawn picture matches the tile's reported numbers
// exactly; only the sample series are recomputed. Returns { available: false } when
// there is no qualifying stable window (score null / no candidates).
export function egtRhythmWaveforms(data, details, options = {}) {
  const best = details?.top?.best;
  if (!Array.isArray(data) || !best || !Number.isFinite(best.startTime) || !Number.isFinite(best.endTime)) {
    return { available: false };
  }
  const { startTime, endTime } = best;
  const allCandidates = Array.isArray(details.candidates) ? details.candidates : [];
  // The cylinders measured in the detector's chosen window (one best per cylinder).
  const windowCandidates = allCandidates
    .filter((candidate) => candidate.startTime === startTime && candidate.endTime === endTime && Number.isFinite(candidate.amplitude));
  if (!windowCandidates.length) return { available: false };
  // commonEGT is averaged over every cylinder the detector measured (non-finite
  // values drop out of averageValues, so the exact key set only matters for
  // partially-present cylinders — match the detector's breadth via the union).
  const egtKeys = [...new Set(allCandidates.map((candidate) => candidate.key))];

  const windowPoints = data.filter((point) => {
    const t = point.elapsedSeconds;
    return Number.isFinite(t) && t >= startTime && t <= endTime;
  });
  if (windowPoints.length < egtRhythmParameters.oscillation.minSamples) return { available: false };
  const t0 = windowPoints[0].elapsedSeconds ?? 0;

  const cylinders = windowCandidates.map((candidate) => {
    const pairs = windowPoints.map((point, index) => {
      const value = point[candidate.key];
      const common = averageValues(pointCylinderValues(point, egtKeys));
      if (!Number.isFinite(value) || !Number.isFinite(common)) return null;
      return { t: (point.elapsedSeconds ?? index) - t0, value, residual: value - common };
    }).filter(Boolean);
    if (pairs.length < egtRhythmParameters.oscillation.minSamples) return null;
    const residualValues = smoothSeries(
      linearDetrend(pairs.map((pair) => ({ t: pair.t, value: pair.residual }))),
      egtRhythmParameters.smoothingRadius,
    );
    const residualSeries = pairs
      .map((pair, index) => ({ t: pair.t, value: residualValues[index] }))
      .filter((point) => Number.isFinite(point.value));
    if (!residualSeries.length) return null;
    const baselineValues = smoothSeries(
      linearDetrend(pairs.map((pair) => ({ t: pair.t, value: pair.value }))),
      egtRhythmParameters.smoothingRadius,
    );
    const baselineSeries = pairs
      .map((pair, index) => ({ t: pair.t, value: baselineValues[index] }))
      .filter((point) => Number.isFinite(point.value));
    // Fit curve at the candidate's dominant frequency (mean-subtracted residual).
    const mean = averageValues(residualSeries.map((point) => point.value)) || 0;
    const fitFn = fitCurveAtFrequency(
      residualSeries.map((point) => ({ t: point.t, y: point.value - mean })),
      candidate.frequency?.frequencyCpm,
    );
    const fitSeries = fitFn ? residualSeries.map((point) => ({ t: point.t, value: mean + fitFn(point.t) })) : [];
    return {
      cylinder: candidate.cylinder,
      key: candidate.key,
      label: options.labelForKey?.(candidate.key) ?? `Cyl ${candidate.cylinder}`,
      residualSeries,
      baselineSeries,
      fitSeries,
      amplitude: candidate.amplitude,
      ratio: candidate.ratio,
      frequencyCpm: candidate.frequency?.frequencyCpm ?? null,
      cycleSeconds: candidate.frequency?.cycleSeconds ?? null,
      cyclesInWindow: candidate.frequency?.cyclesInWindow ?? null,
      band: candidate.frequency?.band ?? "unclassified",
      qualifies: candidate.suspicion > 0,
    };
  }).filter(Boolean).sort((a, b) => a.cylinder - b.cylinder);

  if (!cylinders.length) return { available: false };
  return {
    available: true,
    window: { startTime, endTime, durationSeconds: Math.max(1, endTime - startTime) },
    floorF: egtRhythmParameters.candidate.minAmplitudeF,
    warningF: egtRhythmParameters.severity.warningAmplitudeF,
    worstCylinder: details.top.cylinder,
    cylinders,
  };
}
